import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Shield, LogIn, User, LogOut, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { GlobalLanguageSelector } from "@/components/GlobalLanguageSelector";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user: patientUser, signOut: patientSignOut, loading: patientLoading } = useAuth();
  const { user: adminUser, signOut: adminSignOut, loading: adminLoading } = useAdminAuth();
  const navigate = useNavigate();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Prefer admin user context over patient context
  const currentUser = adminUser || patientUser;
  const isLoading = patientLoading || adminLoading;
  // Only use patientUser role if no adminUser is present to avoid stale data
  const currentRole = adminUser ? adminUser.role : patientUser?.role;

  const handleSignOut = async () => {
    try {
      // Important: use context signOut so admin logout is audit-logged
      if (adminUser) await adminSignOut();
      if (patientUser) await patientSignOut();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const getUserInitials = () => {
    if (adminUser) {
      return adminUser.email?.[0]?.toUpperCase() || "A";
    }
    if (patientUser?.patient) {
      return `${patientUser.patient.first_name?.[0] || ""}${patientUser.patient.last_name?.[0] || ""}`.toUpperCase() || "U";
    }
    return patientUser?.email?.[0]?.toUpperCase() || "U";
  };

  const getUserDisplayName = () => {
    if (adminUser) {
      return adminUser.email || "Admin";
    }
    if (patientUser?.patient) {
      return `${patientUser.patient.first_name} ${patientUser.patient.last_name}`;
    }
    return patientUser?.email || "User";
  };

  const getRoleDisplayName = () => {
    if (!currentRole) return currentUser ? "Loading…" : "User";
    return currentRole.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <header className="sticky top-0 z-50 w-full glass-header border-b border-white/10">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 transition-smooth hover:opacity-80">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl overflow-hidden">
              <img 
                src="/lovable-uploads/4286fffc-4262-4f0a-be42-44baab963afa.png" 
                alt="CheckFirst Care Logo - AI-powered healthcare triage platform" 
                className="h-12 w-12 object-contain" 
                width={48}
                height={48}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-white">CheckFirst</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" aria-label="Main navigation">
            <Link
              to="/" 
              className="text-white/80 hover:text-white transition-smooth font-medium text-sm"
            >
              Home
            </Link>
            <Link 
              to="/triage" 
              className="text-white/80 hover:text-white transition-smooth font-medium text-sm"
            >
              Symptom Checker
            </Link>
            <Link 
              to="/about" 
              className="text-white/80 hover:text-white transition-smooth font-medium text-sm"
            >
              About
            </Link>
            <Link 
              to="/founder" 
              className="text-white/80 hover:text-white transition-smooth font-medium text-sm"
            >
              Founder
            </Link>
            <Link 
              to="/contact" 
              className="text-white/80 hover:text-white transition-smooth font-medium text-sm"
            >
              Contact
            </Link>
            <Link 
              to="/blog" 
              className="text-white/80 hover:text-white transition-smooth font-medium text-sm"
            >
              Blog
            </Link>
          </nav>

          {/* Trust Badge & Auth/CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <GlobalLanguageSelector variant="header" />
            <ThemeSwitcher />
            <div className="trust-badge" role="status" aria-label="HIPAA Compliant security badge">
              <Shield className="h-3 w-3" aria-hidden="true" />
              <span className="text-xs">HIPAA Compliant</span>
            </div>
            
            {!isLoading && currentUser ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-white/10">
                    <Avatar className="h-9 w-9 border-2 border-white/20">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover border border-border">
                  <div className="flex items-center gap-2 p-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-foreground">{getUserDisplayName()}</p>
                      <p className="text-xs text-muted-foreground">{getRoleDisplayName()}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile-settings" className="flex items-center gap-2 cursor-pointer">
                      <User className="h-4 w-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive">
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" asChild className="text-white/80 hover:text-white hover:bg-white/10">
                <Link to="/login" className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  Login
                </Link>
              </Button>
            )}
            
            <Button variant="secondary" size="sm" asChild className="hover-lift bg-white text-purple-900 hover:bg-white/90">
              <Link to="/triage">Start Assessment</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-xl hover:bg-white/10 transition-smooth text-white"
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div id="mobile-menu" className="md:hidden py-6 border-t border-white/10 animate-fade-in">
            <nav className="flex flex-col space-y-4" aria-label="Mobile navigation">
              <Link 
                to="/" 
                className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/triage" 
                className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Symptom Checker
              </Link>
              <Link 
                to="/about" 
                className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/founder" 
                className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Founder
              </Link>
              <Link 
                to="/contact" 
                className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <Link 
                to="/blog" 
                className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              
              {!isLoading && currentUser ? (
                <>
                  <div className="flex items-center gap-3 py-2 px-2">
                    <Avatar className="h-10 w-10 border-2 border-white/20">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <p className="text-white font-medium">{getUserDisplayName()}</p>
                      <p className="text-white/60 text-sm">{getRoleDisplayName()}</p>
                    </div>
                  </div>
                  <Link 
                    to="/dashboard" 
                    className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10 flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                  <Link 
                    to="/profile-settings" 
                    className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10 flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="h-4 w-4" />
                    Profile Settings
                  </Link>
                  <button 
                    onClick={() => { handleSignOut(); setIsMenuOpen(false); }}
                    className="text-red-400 hover:text-red-300 transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10 flex items-center gap-2 w-full text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="text-white/80 hover:text-white transition-smooth font-medium py-2 px-2 rounded-lg hover:bg-white/10 flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <LogIn className="h-4 w-4" />
                  Login
                </Link>
              )}
              
              <div className="pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <GlobalLanguageSelector variant="header" />
                  <ThemeSwitcher />
                  <div className="trust-badge">
                    <Shield className="h-3 w-3" />
                    <span className="text-xs">HIPAA Compliant</span>
                  </div>
                </div>
                
                <Button variant="secondary" className="w-full hover-lift bg-white text-purple-900 hover:bg-white/90" asChild>
                  <Link to="/triage" onClick={() => setIsMenuOpen(false)}>
                    Start Assessment
                  </Link>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};